import React from 'react'
import { Flex, Image, Text } from '@common/fundamentals'
import { Button } from '@common/material'
import { FileDownloadOutlined } from '@mui/icons-material'
import { localizedStrings } from '@core/strings'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument/SiteBoxDocument'
import PspdfkitComponent, {
  PspdfkitComponentRef,
} from 'components/modal/pdf/PspdfkitComponent'

type SiteBoxDocumentPreviewProps = {
  // The document to preview. Optional: the viewer modal is also opened for a
  // bare PDF proof that has no SiteBox document — only a URL (see `documentUrl`).
  siteBoxDocument?: SiteBoxDocument
  // Required for the doc-less case; also lets callers pass a pre-resolved URL (e.g. a proof uri).
  documentUrl?: string
  name?: string
  // PDF-only: the modal forwards a ref so its sidebar can drive annotations,
  // and renders read-only. The full-page view omits both (editable, no ref).
  pspdfkitRef?: React.Ref<PspdfkitComponentRef>
  readOnly?: boolean
}

export const SiteBoxDocumentPreview: React.FC<SiteBoxDocumentPreviewProps> = ({
  siteBoxDocument,
  documentUrl,
  name,
  pspdfkitRef,
  readOnly,
}) => {
  const version = siteBoxDocument?.currentVersion
  const previewMode = version?.getPreviewMode?.() ?? 'PDF'
  const url = documentUrl ?? version?.documentGetUrl
  const downloadUrl = version?.documentDownloadUrl ?? url
  // Without a URL the media tags / PSPDFKit would render broken; fall back to
  // the download-only view (which itself disables when there's nothing to fetch).
  const effectivePreviewMode = url ? previewMode : 'DOWNLOAD'

  switch (effectivePreviewMode) {
    case 'IMAGE':
      return (
        <Flex
          width='100%'
          height='100%'
          justifyContent='center'
          alignItems='center'
        >
          <Image
            src={url}
            alt={name ?? ''}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Flex>
      )
    case 'VIDEO':
      return (
        <Flex
          width='100%'
          height='100%'
          justifyContent='center'
          alignItems='center'
        >
          <video
            src={url}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
            }}
          />
        </Flex>
      )
    case 'DOWNLOAD':
      return (
        <Flex
          width='100%'
          height='100%'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
        >
          <Text mb={2} color='grey.600'>
            {localizedStrings.noPreviewAvailable}
          </Text>
          <Button
            variant='contained'
            startIcon={<FileDownloadOutlined />}
            disabled={!downloadUrl}
            onClick={() =>
              downloadUrl &&
              window.open(downloadUrl, '_blank', 'noopener,noreferrer')
            }
          >
            {localizedStrings.download}
          </Button>
        </Flex>
      )
    default:
      return (
        <Flex width='100%' height='100%'>
          <PspdfkitComponent
            ref={pspdfkitRef}
            document={url}
            readOnly={readOnly}
          />
        </Flex>
      )
  }
}
